import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Trash2, Plus, Edit3, Save, X, ImageIcon } from 'lucide-react';

const ArtistsManager = ({ token }) => {
  const [artists, setArtists] = useState([]);
  const [draft, setDraft] = useState({ name: '', photo_url: '', bio: '' });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', photo_url: '', bio: '' });
  const [loading, setLoading] = useState(false);

  const fetchArtists = async () => {
    try {
      const res = await axios.get(`${API}/artists`);
      setArtists(res.data || []);
    } catch (e) {
      toast.error('Échec du chargement des artistes');
    }
  };

  useEffect(() => {
    fetchArtists();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/artists`, draft, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Artiste ajouté');
      setDraft({ name: '', photo_url: '', bio: '' });
      fetchArtists();
    } catch {
      toast.error("Échec de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet artiste ?')) return;
    try {
      await axios.delete(`${API}/artists/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Artiste supprimé');
      fetchArtists();
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  const startEdit = (artist) => {
    setEditingId(artist.id);
    setEditDraft({
      name: artist.name || '',
      photo_url: artist.photo_url || '',
      bio: artist.bio || '',
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/artists/${editingId}`, editDraft, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Artiste mis à jour');
      setEditingId(null);
      fetchArtists();
    } catch {
      toast.error('Échec de la mise à jour');
    }
  };

  return (
    <div className="resource-manager" data-testid="artists-manager">
      <Card className="add-video-card">
        <h3>Ajouter un artiste</h3>
        <form onSubmit={handleAdd} className="add-video-form">
          <Input
            placeholder="Nom de l'artiste"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="artist-name-input"
          />
          <Input
            placeholder="URL de la photo (https://…)"
            value={draft.photo_url}
            onChange={(e) => setDraft({ ...draft, photo_url: e.target.value })}
            data-testid="artist-photo-input"
          />
          <textarea
            className="bio-textarea"
            rows={3}
            placeholder="Petite biographie (facultatif)"
            value={draft.bio}
            onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
            data-testid="artist-bio-input"
          />
          <Button type="submit" disabled={loading} data-testid="add-artist-button">
            <Plus size={18} />
            Ajouter l'artiste
          </Button>
        </form>
      </Card>

      <Card className="videos-list-card">
        <h3>Artistes ({artists.length})</h3>
        {artists.length === 0 ? (
          <div className="empty-state">
            <p>Aucun artiste pour l'instant. Ajoutez-en un ci-dessus.</p>
          </div>
        ) : (
          <div className="resource-grid">
            {artists.map((a, idx) => (
              <div key={a.id} className="resource-card" data-testid={`artist-card-${idx}`}>
                {editingId === a.id ? (
                  <div className="resource-edit">
                    <Input
                      value={editDraft.name}
                      onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                    />
                    <Input
                      value={editDraft.photo_url}
                      onChange={(e) => setEditDraft({ ...editDraft, photo_url: e.target.value })}
                      placeholder="URL photo"
                    />
                    <textarea
                      className="bio-textarea"
                      rows={3}
                      value={editDraft.bio}
                      onChange={(e) => setEditDraft({ ...editDraft, bio: e.target.value })}
                    />
                    <div className="resource-edit-actions">
                      <Button size="sm" onClick={saveEdit} data-testid={`save-artist-${idx}`}>
                        <Save size={14} /> Enregistrer
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        <X size={14} /> Annuler
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="resource-photo-wrap">
                      {a.photo_url ? (
                        <img src={a.photo_url} alt={a.name} className="resource-photo" />
                      ) : (
                        <div className="resource-photo placeholder">
                          <ImageIcon size={32} />
                        </div>
                      )}
                    </div>
                    <div className="resource-body">
                      <h4>{a.name}</h4>
                      {a.bio && <p className="resource-bio">{a.bio}</p>}
                    </div>
                    <div className="resource-actions">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(a)} data-testid={`edit-artist-${idx}`}>
                        <Edit3 size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(a.id)}
                        data-testid={`delete-artist-${idx}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ArtistsManager;
